import { Injectable } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { EditEmpresaService } from './edit-empresa.service';
import { opciones } from '../assets/dataEslabon';
import { Empresa } from '../interfaces/empresa.interface';
import { RegistroService } from 'src/app/registro.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class EmpresaHelperService {
    opciones = opciones;
    selectedProductos: string[] = [];
    selectedEslabon: string[] = [];
    private apiUrl: string = environment.apiSiriUrl;

    constructor(
        private editarService: EditEmpresaService,
        private registroService: RegistroService,
        private http: HttpClient
    ) {}

    async getEmpresa(userId: string): Promise<Empresa> {
        const endpoint = userId
            ? `agente-vigilado/${userId}`
            : `agente-vigilado/${this.registroService.empresaSelected}`;
        const url = `${this.apiUrl}${endpoint}`;
        const response = firstValueFrom(await this.http.get<Empresa>(url));
        return response;
    }

    /**
     * Reinicia el estado de las selecciones de productos y eslabones almacenadas en el servicio.
     *
     * Este método es fundamental para limpiar los arreglos `selectedProductos` y `selectedEslabon`.
     * Su propósito es evitar que las selecciones de un formulario de empresa anterior
     * persistan cuando el usuario intenta registrar una nueva, garantizando así un
     * estado limpio y previniendo la reaparición de datos residuales.
     * @returns {void} No retorna ningún valor.
     */
    public reset(): void {
        this.selectedProductos = [];
        this.selectedEslabon = [];
    }

    addProducto(value: string, empresa: any): string[] {
        if (!this.selectedProductos.includes(value)) {
            this.selectedProductos.push(value);
            this.updateTipoProducto(empresa);
        }
        return this.selectedProductos;
    }

    removeProducto(value: string, empresa: any): string[] {
        this.selectedProductos = this.selectedProductos.filter(
            (item) => item !== value
        );
        this.updateTipoProducto(empresa);
        return this.selectedProductos;
    }

    async updateTipoProducto(empresa: any) {
        const tipoProducto = this.selectedProductos.join(' y ');
        empresa.get('tipo_producto_que_importa').patchValue(tipoProducto);
    }

    addEslabon(value: string, empresa: any): string[] {
        if (!this.selectedEslabon.includes(value)) {
            this.selectedEslabon.push(value);
            this.updateTipoEslabon(empresa);
        }
        return this.selectedEslabon;
    }

    removeEslabon(value: string, empresa: any): string[] {
        this.selectedEslabon = this.selectedEslabon.filter(
            (item) => item !== value
        );
        this.updateTipoEslabon(empresa);
        return this.selectedEslabon;
    }

    updateTipoEslabon(empresa: any): void {
        const tipoProducto = this.selectedEslabon.join('/');
        empresa.get('eslabon').patchValue(tipoProducto);
    }

    visualizarDireccion(empresa) {
        const direccionForm = empresa.get('direccion');

        const direccionArray = [
            direccionForm.get('viaPrincipalTipo').value || '',
            direccionForm.get('viaPrincipalNumero').value || '',
            direccionForm.get('viaPrincipalLetra').value || '',
            direccionForm.get('prefijoBis').value === false ||
            direccionForm.get('prefijoBis').value === null
                ? ''
                : 'Bis',
            direccionForm.get('letraPrefijo').value || '',
            direccionForm.get('viaPrincipalCuadrante').value || '',
            direccionForm.get('viaGeneradoraNumero').value || '',
            direccionForm.get('viaGeneradoraLetra').value || '',
            direccionForm.get('numeroPlaca').value || '',
            direccionForm.get('viaGeneradoraCuadrante').value || '',
        ];

        return direccionArray.join(' ');
    }

    quitarValidador(empresaForm: FormGroup) {
        const direccionFormGroup = empresaForm.get('direccion');
        const zonaControl = direccionFormGroup.get('zona');
        const complementoControl = direccionFormGroup.get('complemento');
        const viaPrincipalTipoControl =
            direccionFormGroup.get('viaPrincipalTipo');
        const viaPrincipalNumeroControl =
            direccionFormGroup.get('viaPrincipalNumero');
        const viaGeneradoraNumeroControl = direccionFormGroup.get(
            'viaGeneradoraNumero'
        );
        const numeroPlacaControl = direccionFormGroup.get('numeroPlaca');
        const prefijoBisControl = direccionFormGroup.get('prefijoBis');
        const letraPrefijoControl = direccionFormGroup.get('letraPrefijo');
        const viaPrincipalCuadranteControl = direccionFormGroup.get(
            'viaPrincipalCuadrante'
        );
        const viaGeneradoraCuadranteControl = direccionFormGroup.get(
            'viaGeneradoraCuadrante'
        );
        const viaGeneradoraLetraControl =
            direccionFormGroup.get('viaGeneradoraLetra');
        const viaPrincipalLetraControl =
            direccionFormGroup.get('viaPrincipalLetra');

        // Aplicar validadores según la zona
        if (zonaControl.value === 'Urbana') {
            [
                viaPrincipalTipoControl,
                viaPrincipalNumeroControl,
                viaGeneradoraNumeroControl,
                numeroPlacaControl,
            ].forEach((control) => {
                control.setValidators([Validators.required]);
                control.updateValueAndValidity();
            });
            complementoControl.clearValidators();
            complementoControl.updateValueAndValidity();
            complementoControl.reset();
        } else if (zonaControl.value === 'Rural') {
            complementoControl.setValidators([Validators.required]);
            complementoControl.updateValueAndValidity();

            [
                viaPrincipalTipoControl,
                viaPrincipalNumeroControl,
                viaGeneradoraNumeroControl,
                numeroPlacaControl,
                prefijoBisControl,
                letraPrefijoControl,
                viaPrincipalCuadranteControl,
                viaGeneradoraCuadranteControl,
                viaGeneradoraLetraControl,
                viaPrincipalLetraControl,
            ].forEach((control) => {
                control.clearValidators();
                control.updateValueAndValidity();
                control.reset();
            });
        }
    }
}
